import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { SENTRY_DSN, SENTRY_ENVIRONMENT } from "./env";

declare const __SENTRY_RELEASE__: string;

export function initSentry(): void {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release:
      typeof __SENTRY_RELEASE__ !== "undefined"
        ? __SENTRY_RELEASE__
        : undefined,

    // Performance: sample 10% in production, 100% elsewhere
    tracesSampleRate: SENTRY_ENVIRONMENT === "production" ? 0.1 : 1.0,

    // Session replay disabled — captures PII by nature
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Structured logging
    enableLogs: true,

    integrations: [
      // React Router v7 integration — names transactions by route path
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Forward console.warn and console.error as Sentry logs
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],

    // ── PII Protection (client-side, before data leaves the browser) ──
    sendDefaultPii: false,

    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      if (event.request) {
        delete event.request.cookies;
        delete event.request.data;
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      // Drop UI input breadcrumbs that could contain typed PII
      if (breadcrumb.category === "ui.input") {
        return null;
      }
      return breadcrumb;
    },
  });
}
